import React from 'react';
import PropTypes from 'prop-types';
import { FaBook, FaGlobeEurope, FaMusic } from 'react-icons/fa';
import { useTranslation } from '../../utils/translations';
import './StudentGroupedView.css';

const iconForActivity = (id) => {
  switch (id) {
    case 'sundaySchool':
      return <FaBook aria-hidden="true" />;
    case 'russianSchool':
      return <FaGlobeEurope aria-hidden="true" />;
    case 'childrensChoir':
      return <FaMusic aria-hidden="true" />;
    default:
      return null;
  }
};

// Compact formatter that shows all teachers/leaders with proper wrapping
const joinWithLimit = (arr = [], limit = 2) => {
  if (!Array.isArray(arr) || arr.length === 0) return '';
  // Wrap each name in a span to prevent breaking individual names
  const wrappedNames = arr.map(name => `<span style="white-space: nowrap;">${name}</span>`);
  return wrappedNames.join(', ');
};

// Format birthdate for display
const formatBirthdate = (birthdate) => {
  if (!birthdate) return '';
  try {
    const [year, month, day] = birthdate.split('-').map(Number);
    const date = new Date(year, month - 1, day); // local date, not UTC
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  } catch (error) {
    return birthdate; // Return original if parsing fails
  }
};

const StudentGroupedView = ({ students, activities }) => {
  const { t } = useTranslation();
  
  // Helper to get a student's activity details by id
  const getStudentActivity = (student, activityId) =>
    student.activities.find((sa) => sa.id === activityId);

  return (
    <div className="student-grouped-layout" role="main" aria-label="Students grouped view">
      {students.map((student) => {
        // Count how many activities this student is enrolled in
        const enrolledActivities = ['sundaySchool', 'russianSchool', 'childrensChoir'].filter((aid) => {
          const sa = getStudentActivity(student, aid);
          return sa?.details;
        });

        return (
          <div key={student.id} className="student-container">
            <div className="student-container-header">
              <h3 className="student-title">{student.name}</h3>
              <span className="student-count">
                {enrolledActivities.length} {enrolledActivities.length === 1 ? t('main.activity') : t('main.activities')}
              </span>
            </div>
            {student.birthdate && (
              <div className="student-birthday-info">
                <span className="birthday-label">{t('details.birthday')}:</span>
                <span className="birthday-value">{formatBirthdate(student.birthdate)}</span>
              </div>
            )}
            <div className="activities-in-student">
              {['sundaySchool', 'russianSchool', 'childrensChoir'].map((aid) => {
                const activity = activities.find(a => a.id === aid);
                const sa = getStudentActivity(student, aid);
                
                if (!sa?.details) {
                  return null; // Don't show activities the student isn't enrolled in
                }
                
                const d = sa.details;
                const room = d.classroom || d.choirRoom;
                const main = d.class || d.choirNumber;
                const people = d.teachers || d.choirLeaders;
                const peopleText = joinWithLimit(people, 2);
                const labels = aid === 'childrensChoir'
                  ? { main: t('details.choirNumber'), people: t('details.choirLeaders') }
                  : { main: t('details.class'), people: t('details.teachers') };

                return (
                  <div key={aid} className="activity-in-student-card" data-activity={aid}>
                    <div className="activity-header-info">
                      <div className="activity-name-with-icon">
                        <span className="activity-icon">{iconForActivity(aid)}</span>
                        <h4 className="activity-name">{t(`activities.${aid}`) || activity?.name || aid}</h4>
                      </div>
                      <span className="day-badge">{d.day}</span>
                    </div>
                    <div className="activity-details">
                      <div className="detail-row">
                        <span className="detail-label">{labels.main}:</span>
                        <span className="detail-value">{main}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">{t('details.room')}:</span>
                        <span className="detail-value">{room}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">{labels.people}:</span>
                        <span 
                          className="detail-value" 
                          dangerouslySetInnerHTML={{ __html: peopleText }}
                        ></span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

StudentGroupedView.propTypes = {
  students: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      birthdate: PropTypes.string,
      activities: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          status: PropTypes.string,
          details: PropTypes.object,
        })
      ).isRequired,
    })
  ).isRequired,
  activities: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      day: PropTypes.string,
    })
  ).isRequired,
};

export default StudentGroupedView;

